// components/AdminMessaging/MessageTemplates.tsx
// Template management for quick responses

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    fetchMessageTemplates,
    createMessageTemplate,
    deleteMessageTemplate,
} from '../../services/adminMessaging';
import type { AdminMessageTemplate, CreateTemplateDTO } from '../../types';
import './AdminMessaging.css';

export const MessageTemplates: React.FC = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState<AdminMessageTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);

    // Create form state
    const [newTemplate, setNewTemplate] = useState<Partial<CreateTemplateDTO>>({
        name: '',
        responseType: 'text',
        category: 'custom',
        textContent: '',
    });
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await fetchMessageTemplates();
            setTemplates(data);
        } catch (err: any) {
            console.error('Failed to load templates:', err);
            setError(err.message || 'Failed to load templates');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTemplate = async () => {
        if (!newTemplate.name || !newTemplate.responseType || !newTemplate.category) {
            alert('Please fill in all required fields');
            return;
        }

        if (newTemplate.responseType === 'text' && !newTemplate.textContent) {
            alert('Text content is required for text templates');
            return;
        }

        try {
            setCreating(true);
            await createMessageTemplate(newTemplate as CreateTemplateDTO);
            alert('✅ Template created successfully!');
            setShowCreateForm(false);
            setNewTemplate({
                name: '',
                responseType: 'text',
                category: 'custom',
                textContent: '',
            });
            loadTemplates();
        } catch (err: any) {
            alert(`❌ Failed to create template:\n\n${err.message}`);
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteTemplate = async (templateId: string) => {
        if (!window.confirm('Delete this template? This action cannot be undone.')) {
            return;
        }

        try {
            await deleteMessageTemplate(templateId);
            alert('✅ Template deleted');
            loadTemplates();
        } catch (err: any) {
            alert(`❌ Failed to delete template:\n\n${err.message}`);
        }
    };

    const getCategoryIcon = (category: string) => {
        switch (category) {
            case 'welcome': return '👋';
            case 'support': return '🛟';
            case 'encouragement': return '💪';
            case 'faq': return '❓';
            default: return '📝';
        }
    };

    if (loading) {
        return (
            <div className="messaging-container">
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading templates...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="messaging-container">
            <header className="messaging-header">
                <div className="header-content">
                    <button
                        onClick={() => navigate('/admin-messaging')}
                        className="back-button"
                    >
                        ← Back to Inbox
                    </button>
                    <div>
                        <h1>📝 Message Templates</h1>
                        <p className="header-subtitle">Manage quick response templates</p>
                    </div>
                    <div className="header-actions">
                        <button
                            onClick={() => setShowCreateForm(!showCreateForm)}
                            className="primary-button"
                        >
                            {showCreateForm ? 'Cancel' : '+ Create Template'}
                        </button>
                    </div>
                </div>
            </header>

            <main className="templates-main">
                {error && (
                    <div className="error-banner">
                        <span>⚠️ {error}</span>
                        <button onClick={loadTemplates} className="retry-button">
                            Retry
                        </button>
                    </div>
                )}

                {/* Create Template Form */}
                {showCreateForm && (
                    <section className="create-template-section">
                        <h2>Create New Template</h2>
                        <div className="template-form">
                            <div className="form-group">
                                <label>Template Name *</label>
                                <input
                                    type="text"
                                    value={newTemplate.name}
                                    onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                                    placeholder="e.g., Welcome Message"
                                    className="form-input"
                                />
                            </div>

                            <div className="form-group">
                                <label>Category *</label>
                                <select
                                    value={newTemplate.category}
                                    onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value as any })}
                                    className="form-select"
                                >
                                    <option value="welcome">👋 Welcome</option>
                                    <option value="support">🛟 Support</option>
                                    <option value="encouragement">💪 Encouragement</option>
                                    <option value="faq">❓ FAQ</option>
                                    <option value="custom">📝 Custom</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Response Type *</label>
                                <select
                                    value={newTemplate.responseType}
                                    onChange={(e) => setNewTemplate({ ...newTemplate, responseType: e.target.value as any })}
                                    className="form-select"
                                >
                                    <option value="text">💬 Text</option>
                                    <option value="voice">🎙️ Voice</option>
                                    <option value="image">🖼️ Image</option>
                                    <option value="mixed">🎨 Mixed</option>
                                </select>
                            </div>

                            {newTemplate.responseType === 'text' && (
                                <div className="form-group">
                                    <label>Text Content *</label>
                                    <textarea
                                        value={newTemplate.textContent}
                                        onChange={(e) => setNewTemplate({ ...newTemplate, textContent: e.target.value })}
                                        placeholder="Enter your template message..."
                                        rows={5}
                                        className="form-textarea"
                                    />
                                </div>
                            )}

                            <div className="form-actions">
                                <button
                                    onClick={handleCreateTemplate}
                                    disabled={creating}
                                    className="primary-button"
                                >
                                    {creating ? 'Creating...' : 'Create Template'}
                                </button>
                                <button
                                    onClick={() => setShowCreateForm(false)}
                                    disabled={creating}
                                    className="secondary-button"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </section>
                )}

                {/* Templates List */}
                <section className="templates-list-section">
                    <h2>Saved Templates ({templates.length})</h2>

                    {templates.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">📝</div>
                            <h3>No templates yet</h3>
                            <p>Create your first template to save time when responding to messages</p>
                        </div>
                    ) : (
                        <div className="templates-grid">
                            {templates.map((template) => (
                                <div key={template.id} className="template-card">
                                    <div className="template-header">
                                        <h3>
                                            {getCategoryIcon(template.category)} {template.name}
                                        </h3>
                                        <button
                                            onClick={() => handleDeleteTemplate(template.id)}
                                            className="delete-button"
                                            title="Delete template"
                                        >
                                            🗑️
                                        </button>
                                    </div>

                                    <div className="template-meta">
                                        <span className="template-type">
                                            {template.responseType === 'text' ? '💬' :
                                             template.responseType === 'voice' ? '🎙️' :
                                             template.responseType === 'image' ? '🖼️' : '🎨'}
                                            {' '}
                                            {template.responseType.charAt(0).toUpperCase() + template.responseType.slice(1)}
                                        </span>
                                        <span className="template-category">
                                            {template.category}
                                        </span>
                                    </div>

                                    {template.textContent && (
                                        <p className="template-preview">
                                            {template.textContent.length > 150
                                                ? `${template.textContent.substring(0, 150)}...`
                                                : template.textContent}
                                        </p>
                                    )}

                                    <div className="template-stats">
                                        <span>Used {template.usageCount || 0} times</span>
                                        {template.lastUsedAt && (
                                            <span>Last used: {new Date(template.lastUsedAt).toLocaleDateString()}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

export default MessageTemplates;
